import type {
  HelpHubBranding,
  HelpHubCustomSection,
} from "@/types/help-hub/help-hub";
import { HubSection } from "./HubSection";
import { CopyableEmail } from "@/components/common/CopyableEmail";

interface HubCustomSectionsProps {
  sections: HelpHubCustomSection[];
  branding: HelpHubBranding;
}

/**
 * Work out how a field value should behave when tapped. Values are plain text
 * written by the owner; a URL, email address or phone number becomes the link
 * a visitor expects it to be, everything else stays text.
 */
const hrefFor = (value: string): string | null => {
  if (/^https?:\/\/\S+$/i.test(value)) return value;
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return `mailto:${value}`;
  if (/^\+?[\d][\d\s\-()]{5,}$/.test(value)) return `tel:${value.replace(/[^\d+]/g, "")}`;
  return null;
};

/**
 * The owner's own bands — the details a business insists on showing that no
 * fixed band carries (a street address, a second phone line, account details,
 * holiday hours). Renders with the same shell as every built-in section, so
 * an owner-written section is indistinguishable from one we shipped.
 */
export const HubCustomSections = ({ sections, branding }: HubCustomSectionsProps) => {
  if (sections.length === 0) return null;

  return (
    <>
      {sections.map((section) => (
        <HubSection
          key={section.id}
          id={`custom-${section.id}`}
          title={section.title}
          subtitle={section.intro ?? undefined}
          genieIcon="document"
          branding={branding}
        >
          {section.fields.length > 0 && (
            <div className="divide-y divide-genie-border rounded-2xl border border-genie-border bg-white px-4">
              {section.fields.map((field) => {
                const href = hrefFor(field.value);
                return (
                  <div
                    key={field.id}
                    className="flex items-center justify-between gap-4 py-3.5"
                  >
                    <span className="text-[13px] text-genie-text-muted">{field.label}</span>
                    {href?.startsWith("mailto:") ? (
                      <span className="min-w-0 text-right text-[14px] font-semibold" style={{ color: branding.primaryColor }}>
                        <CopyableEmail email={field.value} href={href} />
                      </span>
                    ) : href ? (
                      <a
                        href={href}
                        target={href.startsWith("http") ? "_blank" : undefined}
                        rel={href.startsWith("http") ? "noreferrer noopener" : undefined}
                        className="truncate text-right text-[14px] font-semibold"
                        style={{ color: branding.primaryColor }}
                      >
                        {field.value}
                      </a>
                    ) : (
                      <span className="text-right text-[14px] font-semibold text-genie-text-primary">
                        {field.value}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </HubSection>
      ))}
    </>
  );
};

export default HubCustomSections;
